import { Component, computed, ElementRef, HostListener, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Vehicle } from '@graphql/generated';
import { VehicleService } from '@services/vehicle.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserProfileComponent } from '../../../auth/user-profile/user-profile.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [FormsModule, MatIconModule, UserProfileComponent]
})
export class HeaderComponent {
  searchText = '';
  results = signal<Vehicle[]>([]);
  showResults = signal(false);
  isSearching = signal(false);
  activeIndex = signal<number>(-1);

  liveAnnouncement = computed(() => {
    if (this.isSearching()) return 'Searching...';
    if (!this.showResults()) return '';
    const count = this.results().length;
    if (count === 0) return 'No results found';
    return `${count} result${count === 1 ? '' : 's'} available. Use arrow keys to navigate.`;
  });

  @ViewChild('searchInput') private searchInputRef!: ElementRef<HTMLInputElement>;

  private searchSubject = new Subject<string>();

  constructor(
    private vehicleService: VehicleService,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((term) => term.length >= 2),
      switchMap((term) => {
        this.isSearching.set(true);
        return this.vehicleService.searchVehicles(term);
      }),
      takeUntilDestroyed(),
    ).subscribe({
      next: (results) => {
        this.results.set(results);
        this.activeIndex.set(-1);
        this.showResults.set(true);
        this.isSearching.set(false);
      },
      error: () => {
        this.results.set([]);
        this.activeIndex.set(-1);
        this.isSearching.set(false);
      },
    });
  }

  onSearchInput(): void {
    if (this.searchText.length < 2) {
      this.results.set([]);
      this.activeIndex.set(-1);
      this.showResults.set(false);
      return;
    }
    this.searchSubject.next(this.searchText);
  }

  onKeydown(event: KeyboardEvent): void {
    const count = this.results().length;
    const panelOpen = this.showResults() && count > 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!panelOpen) {
          if (count > 0) this.showResults.set(true);
          return;
        }
        this.activeIndex.set((this.activeIndex() + 1) % count);
        this.scrollActiveIntoView();
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!panelOpen) return;
        this.activeIndex.set(this.activeIndex() <= 0 ? count - 1 : this.activeIndex() - 1);
        this.scrollActiveIntoView();
        break;

      case 'Enter':
        if (panelOpen && this.activeIndex() >= 0) {
          event.preventDefault();
          this.selectResult(this.results()[this.activeIndex()]);
        } else if (this.searchText.length >= 2) {
          this.searchSubject.next(this.searchText);
        }
        break;

      case 'Escape':
        if (this.showResults()) {
          event.preventDefault();
          event.stopPropagation();
          this.closePanel();
        }
        break;

      case 'Tab':
        this.closePanel();
        break;
    }
  }

  /** mousedown (not click): focusout would close the panel before click fires. */
  onResultMouseDown(event: MouseEvent, vehicle: Vehicle): void {
    if (event.button !== 0) return;
    event.preventDefault();
    this.selectResult(vehicle);
  }

  selectResult(vehicle: Vehicle): void {
    this.closePanel();
    this.searchText = '';
    void this.router.navigate(['/maintenance', vehicle.id]);
  }

  onWrapperFocusOut(event: FocusEvent): void {
    const wrapper = (event.currentTarget as HTMLElement);
    const newTarget = event.relatedTarget as Node | null;
    if (!newTarget || !wrapper.contains(newTarget)) {
      this.closePanel();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  private closePanel(): void {
    this.showResults.set(false);
    this.activeIndex.set(-1);
  }

  private scrollActiveIntoView(): void {
    requestAnimationFrame(() => {
      const id = 'search-result-' + this.activeIndex();
      const el = document.getElementById(id);
      el?.scrollIntoView({ block: 'nearest' });
    });
  }
}
